import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Settings, ChevronLeft, Save, DollarSign, TrendingUp, TrendingDown,
  CheckCircle2, XCircle, Wallet, AlertCircle,
} from "lucide-react";
import PrivateLayout from "../components/PrivateLayout";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../services/api";

/**
 * AdminPaymentCore — Configuration providers + transactions + retraits + stats (V3.5).
 * Route additive : /admin/payment-core (ne modifie pas /admin/payments existant).
 */
export default function AdminPaymentCore() {
  const { user } = useAuth();
  const isAdmin = ["admin", "super-admin"].includes(user?.role);
  const [tab, setTab] = useState("config");
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [txs, setTxs] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [stats, setStats] = useState(null);
  const [saving, setSaving] = useState(false);
  const [wdFilter, setWdFilter] = useState("pending");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, t, w, s] = await Promise.all([
        api.get("/v1/payments/admin/config"),
        api.get("/v1/payments/admin/transactions", { params: { limit: 200 } }),
        api.get("/v1/payments/admin/withdrawals", { params: wdFilter ? { status: wdFilter } : {} }),
        api.get("/v1/payments/admin/stats"),
      ]);
      setConfig(c.data.config);
      setTxs(t.data.items || []);
      setWithdrawals(w.data.items || []);
      setStats(s.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [wdFilter]);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  const toggleProvider = async (code, enabled) => {
    setSaving(true);
    try {
      const { data } = await api.patch("/v1/payments/admin/config", { provider: code, provider_patch: { enabled } });
      setConfig(data.config);
      toast.success(`${code} ${enabled ? "activé" : "désactivé"}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const setKeys = async (code, patch) => {
    setSaving(true);
    try {
      const { data } = await api.patch("/v1/payments/admin/config", { provider: code, provider_patch: patch });
      setConfig(data.config);
      toast.success(`Clés ${code} enregistrées`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const toggleOperator = async (op, enabled) => {
    setSaving(true);
    try {
      const { data } = await api.patch("/v1/payments/admin/config", {
        mobile_money_operators: { [op]: { enabled } },
      });
      setConfig(data.config);
      toast.success(`${op} ${enabled ? "activé" : "désactivé"}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const actionWithdrawal = async (id, action) => {
    if (!window.confirm(`Confirmer ${action} ?`)) return;
    try {
      await api.patch(`/v1/payments/admin/withdrawals/${id}`, { action });
      toast.success("Retrait mis à jour");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!isAdmin) {
    return <PrivateLayout title="Payment Core"><div className="afr-empty" data-testid="paymentcore-forbidden"><div className="afr-empty__title">Accès restreint</div></div></PrivateLayout>;
  }

  return (
    <PrivateLayout
      title="💳 Payment Core AFRICTAL"
      breadcrumbs={[{ to: "/admin", label: "Admin" }, { label: "Payment Core" }]}
      actions={<Link to="/admin" className="afr-btn afr-btn--outline afr-btn--sm"><ChevronLeft size={14} /> Admin</Link>}
    >
      <div style={{ display: "flex", gap: 6, marginBottom: 20, borderBottom: "1px solid var(--afr-border)", flexWrap: "wrap" }} data-testid="paymentcore-tabs">
        {[
          { k: "config", label: "Configuration", Icon: Settings },
          { k: "stats", label: "Statistiques", Icon: TrendingUp },
          { k: "transactions", label: `Transactions (${txs.length})`, Icon: DollarSign },
          { k: "withdrawals", label: `Retraits (${withdrawals.length})`, Icon: TrendingDown },
        ].map((t) => (
          <button
            key={t.k} type="button" onClick={() => setTab(t.k)} data-testid={`pc-tab-${t.k}`}
            style={{
              padding: "10px 14px", border: 0, background: "transparent", cursor: "pointer",
              borderBottom: tab === t.k ? "3px solid var(--afr-primary)" : "3px solid transparent",
              color: tab === t.k ? "var(--afr-secondary)" : "#64748b",
              fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6,
            }}
          >
            <t.Icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {loading && <div data-testid="paymentcore-loading">Chargement…</div>}

      {!loading && tab === "config" && config && (
        <ConfigTab config={config} toggleProvider={toggleProvider} setKeys={setKeys} toggleOperator={toggleOperator} saving={saving} />
      )}
      {!loading && tab === "stats" && stats && <StatsTab stats={stats} />}
      {!loading && tab === "transactions" && <TransactionsTab txs={txs} />}
      {!loading && tab === "withdrawals" && (
        <WithdrawalsTab withdrawals={withdrawals} onAction={actionWithdrawal} filter={wdFilter} setFilter={setWdFilter} />
      )}
    </PrivateLayout>
  );
}

function ConfigTab({ config, toggleProvider, setKeys, toggleOperator, saving }) {
  const [flw, setFlw] = useState({ public_key: "", secret_key: "", webhook_secret: "" });
  const providers = config.providers || {};

  return (
    <div data-testid="pc-tab-config-content">
      <p className="afr-muted" style={{ fontSize: "0.86rem", marginBottom: 16 }}>
        Activez ou désactivez les fournisseurs de paiement. Les clés API sont masquées et jamais renvoyées en clair.
      </p>
      <div className="afr-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 14 }}>
        {["wallet", "flutterwave", "mobile_money", "stripe", "wave", "escrow"].map((code) => {
          const p = providers[code] || {};
          return (
            <article key={code} className="afr-card" data-testid={`provider-${code}`} style={{ padding: 16, borderLeft: `4px solid ${p.enabled ? "#15803D" : "#94a3b8"}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                <div>
                  <strong>{p.name || code}</strong>
                  <div className="afr-muted" style={{ fontSize: "0.78rem", marginTop: 2 }}>{(p.supports || []).join(" · ") || "—"}</div>
                </div>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <input type="checkbox" checked={!!p.enabled}
                    onChange={(e) => toggleProvider(code, e.target.checked)}
                    disabled={saving || code === "escrow"} data-testid={`toggle-${code}`} />
                  <span className="afr-muted" style={{ fontSize: "0.82rem" }}>{p.enabled ? "Actif" : "Inactif"}</span>
                </label>
              </div>

              {code === "flutterwave" && (
                <div style={{ marginTop: 12 }}>
                  <div className="afr-muted" style={{ fontSize: "0.78rem", marginBottom: 4 }}>
                    Clé publique : {p.public_key || "—"}<br />Clé secrète : {p.secret_key || "—"}<br />Webhook secret : {p.webhook_secret || "—"}
                  </div>
                  <Row><input placeholder="Public key (FLWPUBK_...)" value={flw.public_key} onChange={(e) => setFlw((s) => ({ ...s, public_key: e.target.value }))} data-testid="flw-pk" /></Row>
                  <Row><input placeholder="Secret key (FLWSECK_...)" value={flw.secret_key} onChange={(e) => setFlw((s) => ({ ...s, secret_key: e.target.value }))} data-testid="flw-sk" /></Row>
                  <Row><input placeholder="Webhook secret" value={flw.webhook_secret} onChange={(e) => setFlw((s) => ({ ...s, webhook_secret: e.target.value }))} data-testid="flw-ws" /></Row>
                  <button type="button" className="afr-btn afr-btn--primary afr-btn--sm" disabled={saving}
                    onClick={() => {
                      const patch = Object.fromEntries(Object.entries(flw).filter(([, v]) => v));
                      if (!Object.keys(patch).length) return toast.error("Renseignez au moins une clé");
                      setKeys("flutterwave", patch);
                      setFlw({ public_key: "", secret_key: "", webhook_secret: "" });
                    }} data-testid="flw-save"><Save size={12} /> Enregistrer les clés</button>
                </div>
              )}

              {code === "mobile_money" && (
                <div style={{ marginTop: 12 }}>
                  <div className="afr-muted" style={{ fontSize: "0.78rem", marginBottom: 6 }}>Opérateurs supportés :</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {Object.entries(p.operators || {}).map(([opCode, op]) => (
                      <label key={opCode} className="afr-badge" style={{
                        background: op.enabled ? "rgba(21,128,61,0.12)" : "rgba(100,116,139,0.10)",
                        color: op.enabled ? "#15803D" : "#64748b", cursor: "pointer",
                      }} data-testid={`op-${opCode}`}>
                        <input type="checkbox" checked={!!op.enabled} onChange={(e) => toggleOperator(opCode, e.target.checked)} disabled={saving} style={{ marginRight: 4 }} />
                        {op.label}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {code === "escrow" && (<div className="afr-muted" style={{ fontSize: "0.82rem", marginTop: 8 }}>Architecture prête. Activation nécessite KYB + accord de licence.</div>)}
            </article>
          );
        })}
      </div>
    </div>
  );
}

function StatsTab({ stats }) {
  return (
    <div data-testid="pc-tab-stats-content">
      <div className="afr-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginBottom: 20 }} data-testid="stats-kpis">
        <Kpi Icon={Wallet} label="Wallets créés" value={stats.wallets_count} accent="#0F2A42" />
        <Kpi Icon={DollarSign} label="Solde cumulé" value={fmt(stats.total_wallet_balance)} accent="#15803D" />
        <Kpi Icon={TrendingUp} label="Dépôts" value={`${stats.deposits.count} · ${fmt(stats.deposits.amount)}`} accent="#009640" />
        <Kpi Icon={TrendingDown} label="Retraits" value={`${stats.withdrawals.count} · ${fmt(stats.withdrawals.amount)}`} accent="#B45309" />
        <Kpi Icon={DollarSign} label="Commissions créditées" value={`${stats.commissions_credited.count} · ${fmt(stats.commissions_credited.amount)}`} accent="#F59E0B" />
        <Kpi Icon={AlertCircle} label="Erreurs" value={stats.errors_count} accent="#B91C1C" />
        <Kpi Icon={AlertCircle} label="Retraits en attente" value={stats.pending_withdrawals} accent="#B45309" />
      </div>
      <article className="afr-card" style={{ padding: 16 }} data-testid="stats-by-provider">
        <strong>Volume par provider</strong>
        <table className="afr-table" style={{ marginTop: 8 }}>
          <thead><tr><th>Provider</th><th style={{ textAlign: "right" }}>Transactions</th><th style={{ textAlign: "right" }}>Volume</th></tr></thead>
          <tbody>
            {Object.entries(stats.by_provider).map(([k, v]) => (
              <tr key={k}><td style={{ textTransform: "capitalize" }}>{k}</td><td style={{ textAlign: "right" }}>{v.count}</td><td style={{ textAlign: "right" }}>{fmt(v.amount)}</td></tr>
            ))}
          </tbody>
        </table>
      </article>
    </div>
  );
}

function TransactionsTab({ txs }) {
  return (
    <div data-testid="pc-tab-transactions-content">
      {txs.length === 0 ? (
        <div className="afr-empty"><div className="afr-empty__title">Aucune transaction</div></div>
      ) : (
        <table className="afr-table">
          <thead><tr><th>Date</th><th>Utilisateur</th><th>Type</th><th>Provider</th><th style={{ textAlign: "right" }}>Montant</th><th>Statut</th></tr></thead>
          <tbody>
            {txs.map((t) => (
              <tr key={t.id} data-testid={`admin-tx-${t.id}`}>
                <td style={{ fontSize: "0.78rem" }}>{new Date(t.created_at).toLocaleString("fr-FR")}</td>
                <td style={{ fontSize: "0.82rem" }}>{t.user_id?.slice(0, 8)}…</td>
                <td>{t.type}</td>
                <td>{t.provider}</td>
                <td style={{ textAlign: "right", fontWeight: 700, color: t.direction === "credit" ? "#15803D" : "#B45309" }}>
                  {t.direction === "credit" ? "+" : "−"} {fmt(t.amount, t.currency)}
                </td>
                <td>{t.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function WithdrawalsTab({ withdrawals, onAction, filter, setFilter }) {
  return (
    <div data-testid="pc-tab-withdrawals-content">
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        {["pending", "approved", "refused", "cancelled", ""].map((f) => (
          <button key={f || "all"} type="button" onClick={() => setFilter(f)}
            className={`afr-btn afr-btn--sm ${filter === f ? "afr-btn--primary" : "afr-btn--outline"}`}
            data-testid={`wd-filter-${f || "all"}`}>{f || "Tous"}</button>
        ))}
      </div>
      {withdrawals.length === 0 ? (
        <div className="afr-empty"><div className="afr-empty__title">Aucune demande</div></div>
      ) : (
        <table className="afr-table">
          <thead><tr><th>Date</th><th>Utilisateur</th><th>Méthode</th><th style={{ textAlign: "right" }}>Montant</th><th>Destination</th><th>Statut</th><th>Actions</th></tr></thead>
          <tbody>
            {withdrawals.map((w) => (
              <tr key={w.id} data-testid={`wd-${w.id}`}>
                <td style={{ fontSize: "0.78rem" }}>{new Date(w.created_at).toLocaleString("fr-FR")}</td>
                <td>{w.user_name || w.user_id?.slice(0, 8)}</td>
                <td>{w.method}{w.operator ? ` · ${w.operator}` : ""}</td>
                <td style={{ textAlign: "right", fontWeight: 700 }}>{fmt(w.amount, w.currency)}</td>
                <td style={{ fontSize: "0.82rem" }}>{w.account_number}</td>
                <td>{w.status}</td>
                <td>
                  {w.status === "pending" && (
                    <div style={{ display: "flex", gap: 4 }}>
                      <button className="afr-btn afr-btn--primary afr-btn--sm" onClick={() => onAction(w.id, "approve")} data-testid={`wd-approve-${w.id}`}><CheckCircle2 size={12} /> Valider</button>
                      <button className="afr-btn afr-btn--outline afr-btn--sm" onClick={() => onAction(w.id, "refuse")} data-testid={`wd-refuse-${w.id}`}><XCircle size={12} /> Refuser</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Row({ children }) {
  return (
    <div className="afr-card" style={{ padding: "0.6rem", marginBottom: "0.5rem" }}>
      <style>{`.afr-card input, .afr-card textarea { width:100%; border:none; outline:none; background:transparent; font-family:'Outfit',sans-serif; font-size:0.9rem; color:var(--afr-secondary); }`}</style>
      {children}
    </div>
  );
}

function Kpi({ Icon, label, value, accent }) {
  return (
    <div className="afr-kpi" style={{ borderLeft: `3px solid ${accent}` }}>
      <div className="afr-kpi__icon" style={{ background: `color-mix(in oklab, ${accent} 14%, transparent)`, color: accent }}><Icon size={18} /></div>
      <span className="afr-kpi__label">{label}</span>
      <span className="afr-kpi__value">{value}</span>
    </div>
  );
}

function fmt(n, cur = "XOF") {
  if (n == null || Number.isNaN(n)) return "—";
  try { return `${new Intl.NumberFormat("fr-FR").format(n)} ${cur}`; } catch { return `${n} ${cur}`; }
}
